import React, { useState, useEffect } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Clock, Calendar, IndianRupee, CreditCard, Copy } from "lucide-react";

export default function SalaryPage() {
  const [salaries, setSalaries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit'
    });
    return formatter.format(new Date());
  });
  const [configModal, setConfigModal] = useState(null);
  const [salaryAmount, setSalaryAmount] = useState("");
  const [weekOff, setWeekOff] = useState("Sunday");
  const [attendanceModal, setAttendanceModal] = useState(null);
  const [attDate, setAttDate] = useState("");
  const [attStatus, setAttStatus] = useState("present");
  const [bankModal, setBankModal] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [salRes, agRes] = await Promise.all([apiCall("get", `/salary/calculate?month=${month}`), apiCall("get", "/agents")]);
      setSalaries(salRes.data.salaries);
      setAgents(agRes.data);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [month]);

  const saveSalaryConfig = async () => {
    if (!configModal || !salaryAmount) { toast.error("Enter salary"); return; }
    try { await apiCall("post", "/salary/set", { agent_id: configModal._id, monthly_salary: parseFloat(salaryAmount), week_off_day: weekOff }); toast.success("Saved"); setConfigModal(null); fetchData(); } catch { toast.error("Failed"); }
  };

  const markPaid = async (agentId) => {
    try { await apiCall("post", "/salary/mark-paid", { agent_id: agentId, month }); toast.success("Marked paid"); fetchData(); } catch { toast.error("Failed"); }
  };

  const saveAttendance = async () => {
    if (!attendanceModal || !attDate) { toast.error("Select date"); return; }
    try { await apiCall("post", "/salary/attendance", { agent_id: attendanceModal.agent_id, date: attDate, status: attStatus }); toast.success("Updated"); setAttendanceModal(null); fetchData(); } catch { toast.error("Failed"); }
  };

  const viewBankDetails = async (agentId) => {
    try {
      const { data } = await apiCall("get", `/salary/bank-details/${agentId}`);
      setBankDetails(data);
      setBankModal(true);
    } catch { toast.error("No bank details found"); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const totalPayable = salaries.reduce((s, a) => s + a.earned_salary, 0);

  return (
    <div className="space-y-6" data-testid="salary-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Salary Management</h2>
          <p className="text-sm text-muted-foreground mt-1">10 AM - 7 PM | 8hr duty + 1hr break | Week-off paid if 6 days worked</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" />
          <Button variant="outline" size="sm" onClick={() => {
            const token = localStorage.getItem("token");
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/salary/download-sheet?month=${month}`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `salary_sheet_${month}.csv`; a.click(); URL.revokeObjectURL(u); });
            toast.success("Downloading...");
          }} data-testid="download-salary-csv"><IndianRupee className="w-3 h-3 mr-1" /> Download CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border border-border"><CardContent className="p-4 flex items-center gap-3"><IndianRupee className="w-8 h-8 text-primary" /><div><p className="text-xs text-muted-foreground uppercase">Total Payable</p><p className="text-xl font-bold">{totalPayable.toFixed(0)} INR</p></div></CardContent></Card>
        <Card className="border border-border"><CardContent className="p-4 flex items-center gap-3"><CheckCircle className="w-8 h-8 text-green-500" /><div><p className="text-xs text-muted-foreground uppercase">Paid</p><p className="text-xl font-bold">{salaries.filter(s => s.payment_status === "paid").length}/{salaries.length}</p></div></CardContent></Card>
        <Card className="border border-border"><CardContent className="p-4 flex items-center gap-3"><Clock className="w-8 h-8 text-yellow-500" /><div><p className="text-xs text-muted-foreground uppercase">Pending</p><p className="text-xl font-bold">{salaries.filter(s => s.payment_status !== "paid").length}</p></div></CardContent></Card>
      </div>

      <Card className="border border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Calendar className="w-4 h-4" /> Salary for {month}</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase">Agent</TableHead>
                <TableHead className="text-xs uppercase">Salary</TableHead>
                <TableHead className="text-xs uppercase">Present</TableHead>
                <TableHead className="text-xs uppercase">Half</TableHead>
                <TableHead className="text-xs uppercase">WO (Paid)</TableHead>
                <TableHead className="text-xs uppercase">Absent</TableHead>
                <TableHead className="text-xs uppercase">Earned</TableHead>
                <TableHead className="text-xs uppercase">Status</TableHead>
                <TableHead className="text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salaries.map((s, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium text-sm">{s.agent_name}</TableCell>
                  <TableCell className="text-sm">{s.monthly_salary ? `${s.monthly_salary}` : <span className="text-muted-foreground italic">-</span>}</TableCell>
                  <TableCell className="text-sm font-medium text-green-600">{s.present_days}</TableCell>
                  <TableCell className="text-sm text-yellow-600">{s.half_days}</TableCell>
                  <TableCell className="text-sm">{s.week_offs} ({s.paid_weekoffs || 0})</TableCell>
                  <TableCell className="text-sm text-red-500">{s.absent_days}</TableCell>
                  <TableCell className="text-sm font-bold">{s.earned_salary} INR</TableCell>
                  <TableCell><Badge className={s.payment_status === "paid" ? "bg-green-500/10 text-green-600" : "bg-yellow-500/10 text-yellow-600"} variant="outline">{s.payment_status}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {s.payment_status !== "paid" && <Button size="sm" variant="ghost" className="text-green-600 text-xs h-7" onClick={() => markPaid(s.agent_id)}>Pay</Button>}
                      <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setConfigModal(agents.find(a => a._id === s.agent_id)); setSalaryAmount(String(s.monthly_salary || "")); }}>Salary</Button>
                      <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setAttendanceModal(s); setAttDate(""); }}>Att.</Button>
                      <Button size="sm" variant="ghost" className="text-xs h-7 text-purple-600" onClick={() => viewBankDetails(s.agent_id)} data-testid={`view-bank-${s.agent_id}`}><CreditCard className="w-3 h-3 mr-1" />Bank</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Salary Config */}
      <Dialog open={!!configModal} onOpenChange={() => setConfigModal(null)}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Set Salary - {configModal?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Monthly Salary (INR)</Label><Input type="number" value={salaryAmount} onChange={e => setSalaryAmount(e.target.value)} placeholder="15000" className="h-9" /></div>
            <div><Label className="text-xs">Week Off Day</Label><Select value={weekOff} onValueChange={setWeekOff}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setConfigModal(null)}>Cancel</Button><Button size="sm" onClick={saveSalaryConfig}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance */}
      <Dialog open={!!attendanceModal} onOpenChange={() => setAttendanceModal(null)}>
        <DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle>Attendance - {attendanceModal?.agent_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={attDate} onChange={e => setAttDate(e.target.value)} className="h-9" /></div>
            <div><Label className="text-xs">Status</Label><Select value={attStatus} onValueChange={setAttStatus}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="present">Present</SelectItem><SelectItem value="absent">Absent</SelectItem><SelectItem value="half_day">Half Day</SelectItem><SelectItem value="week_off">Week Off</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setAttendanceModal(null)}>Cancel</Button><Button size="sm" onClick={saveAttendance}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Details Modal (Admin View + Copy) */}
      <Dialog open={!!bankModal} onOpenChange={() => setBankModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="bank-details-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-purple-500" /> Bank Details - {bankDetails?.name}</DialogTitle></DialogHeader>
          {bankDetails?.bank_details?.account_number ? (
            <div className="space-y-3">
              {[
                { label: "Account Holder", value: bankDetails.bank_details.account_holder },
                { label: "Bank Name", value: bankDetails.bank_details.bank_name },
                { label: "Account Number", value: bankDetails.bank_details.account_number },
                { label: "IFSC Code", value: bankDetails.bank_details.ifsc_code },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-muted">
                  <div><p className="text-[10px] uppercase text-muted-foreground">{item.label}</p><p className="text-sm font-medium">{item.value || "-"}</p></div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyText(item.value || "")}><Copy className="w-3 h-3" /></Button>
                </div>
              ))}
              {bankDetails.bank_details.updated_at && <p className="text-xs text-muted-foreground">Last updated: {bankDetails.bank_details.updated_at.split("T")[0]}</p>}
              <Button variant="outline" size="sm" className="w-full" onClick={() => {
                const text = `Account: ${bankDetails.bank_details.account_holder}\nBank: ${bankDetails.bank_details.bank_name}\nA/C: ${bankDetails.bank_details.account_number}\nIFSC: ${bankDetails.bank_details.ifsc_code}`;
                navigator.clipboard.writeText(text); toast.success("All details copied!");
              }}>Copy All Details</Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Agent has not added bank details yet.</p>
          )}
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setBankModal(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
